/**
 * Haptic Feedback Utility for PWA
 * Uses the Vibration API to provide tactile feedback
 * Falls back gracefully when not supported
 */

// Check if haptic feedback is supported
export const isHapticSupported = () => {
  return 'vibrate' in navigator;
};

// Haptic feedback patterns (in milliseconds)
const PATTERNS = {
  // Light tap - for selections, toggles
  light: [10],
  
  // Medium tap - for button presses, confirmations
  medium: [20],
  
  // Heavy tap - for important actions
  heavy: [30],
  
  // Success - for completed actions
  success: [10, 50, 10],
  
  // Warning - for alerts, confirmations
  warning: [20, 30, 20],
  
  // Error - for errors, failed actions
  error: [40, 30, 40, 30, 40],
  
  // Double tap - for toggles, double confirmations
  double: [10, 50, 10],
  
  // Selection changed
  selection: [5]
};

/**
 * Trigger haptic feedback
 * @param {string|number|number[]} type - Feedback type or custom pattern
 * @returns {boolean} - Whether the feedback was triggered
 */
export const haptic = (type = 'light') => {
  if (!isHapticSupported()) {
    return false;
  }
  
  let pattern;
  
  if (typeof type === 'string') {
    pattern = PATTERNS[type] || PATTERNS.light;
  } else if (typeof type === 'number') {
    pattern = [type];
  } else if (Array.isArray(type)) {
    pattern = type;
  } else {
    pattern = PATTERNS.light;
  }
  
  try {
    return navigator.vibrate(pattern);
  } catch (error) {
    console.warn('Haptic feedback failed:', error);
    return false;
  }
};

/**
 * Cancel any ongoing vibration
 * @returns {boolean} - Whether cancellation was successful
 */
export const cancelHaptic = () => {
  if (!isHapticSupported()) {
    return false;
  }
  
  try {
    return navigator.vibrate(0);
  } catch (error) {
    return false;
  }
};

// Convenience methods for common patterns
export const hapticLight = () => haptic('light');
export const hapticMedium = () => haptic('medium');
export const hapticHeavy = () => haptic('heavy');
export const hapticSuccess = () => haptic('success');
export const hapticWarning = () => haptic('warning');
export const hapticError = () => haptic('error');
export const hapticDouble = () => haptic('double');
export const hapticSelection = () => haptic('selection');

/**
 * React hook for haptic feedback
 * @returns {Object} - Haptic feedback methods
 */
export const useHaptics = () => {
  return {
    isSupported: isHapticSupported(),
    haptic,
    cancelHaptic,
    light: hapticLight,
    medium: hapticMedium,
    heavy: hapticHeavy,
    success: hapticSuccess,
    warning: hapticWarning,
    error: hapticError,
    double: hapticDouble,
    selection: hapticSelection
  };
};

export default haptic;
